import React, { useEffect, useRef, useState } from "react";
import { ChevronsRight, DoorIcon, GateIcon, KeyIcon, LockIcon } from "./accessIcons";
import styles from "../publicvehicle.module.css";

const OPEN_THRESHOLD = 0.85;
const GATE_RESET_MS = 5000;
const HOLD_MS = 1000;
const DOOR_RESET_MS = 10000;

// Clears pending timers and blocks state updates after unmount
const useSafeTimers = () => {
  const mounted = useRef(true);
  const timers = useRef([]);
  useEffect(() => () => {
    mounted.current = false;
    timers.current.forEach(clearTimeout);
  }, []);
  const later = (fn, ms) => {
    const id = setTimeout(() => { if (mounted.current) fn(); }, ms);
    timers.current.push(id);
    return id;
  };
  return { mounted, later };
};

/*
 * Parking gate: drag the key knob to the right end (or press Enter / Space on the track) to open.
 * onOpen() -> Promise<boolean>; the parent shows errors.
 */
export const GateControl = ({ onOpen }) => {
  // idle | opening | opened
  const [phase, setPhase] = useState("idle");
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const knobRef = useRef(null);
  const drag = useRef(null);
  const xRef = useRef(0);
  const { mounted, later } = useSafeTimers();

  const maxX = () => {
    const track = trackRef.current;
    const knob = knobRef.current;
    if (!track || !knob) return 0;
    const padding = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    return Math.max(0, track.clientWidth - knob.offsetWidth - padding * 2);
  };

  const moveTo = (value) => {
    xRef.current = value;
    setX(value);
  };

  const trigger = async () => {
    moveTo(maxX());
    setPhase("opening");
    const ok = await onOpen();
    if (!mounted.current) return;
    if (ok) {
      setPhase("opened");
      later(() => { setPhase("idle"); moveTo(0); }, GATE_RESET_MS);
    } else {
      setPhase("idle");
      moveTo(0);
    }
  };

  const onPointerDown = (e) => {
    if (phase !== "idle") return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startX: e.clientX - xRef.current, max: maxX() };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    moveTo(Math.min(Math.max(e.clientX - drag.current.startX, 0), drag.current.max));
  };

  const endDrag = (e) => {
    if (!drag.current) return;
    const { max } = drag.current;
    drag.current = null;
    setDragging(false);
    if (e.type === "pointerup" && max > 0 && xRef.current >= max * OPEN_THRESHOLD) {
      trigger();
    } else {
      moveTo(0);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (phase === "idle") trigger();
  };

  const active = phase !== "idle";
  const trackText = phase === "opening" ? "Opening…" : phase === "opened" ? "Gate opening — drive through" : "Slide to open";

  return (<div className={styles.gaControl}>
    <div className={styles.gaDevice}>
      <span className={styles.gaDeviceIcon}><GateIcon /></span>
      <div>
        <p className={`${styles.gaStatus} ${active ? styles.gaStatusOn : ""}`}><span className={styles.gaDot} />{active ? "Opening" : "Locked"}</p>
        <p className={styles.gaDeviceName}>Parking Gate</p>
      </div>
    </div>

    <div
      ref={trackRef}
      role="button"
      tabIndex={0}
      aria-label="Slide to open the parking gate"
      aria-disabled={phase !== "idle"}
      className={`${styles.gaTrack} ${phase === "opened" ? styles.gaTrackDone : ""}`}
      onKeyDown={onKeyDown}
    >
      <span className={styles.gaTrackText} aria-live="polite">
        {trackText}{phase === "idle" && <ChevronsRight />}
      </span>
      <span className={styles.gaTrackEnd}><LockIcon size={20} /></span>
      <span
        ref={knobRef}
        className={`${styles.gaKnob} ${dragging ? "" : styles.gaKnobAnimate}`}
        style={{ transform: `translateX(${x}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <KeyIcon />
      </span>
    </div>
  </div>);
};

/*
 * Walk-in door: press and hold the button for 1s to unlock; letting go early cancels.
 * onOpen() -> Promise<boolean>; the parent shows errors.
 */
export const DoorControl = ({ onOpen }) => {
  // idle | holding | unlocking | unlocked
  const [phase, setPhase] = useState("idle");
  const phaseRef = useRef("idle");
  const holdTimer = useRef(null);
  const { mounted, later } = useSafeTimers();

  const setBoth = (value) => {
    phaseRef.current = value;
    setPhase(value);
  };

  const fire = async () => {
    holdTimer.current = null;
    setBoth("unlocking");
    const ok = await onOpen();
    if (!mounted.current) return;
    if (ok) {
      setBoth("unlocked");
      later(() => setBoth("idle"), DOOR_RESET_MS);
    } else {
      setBoth("idle");
    }
  };

  const startHold = () => {
    if (phaseRef.current !== "idle") return;
    setBoth("holding");
    holdTimer.current = later(fire, HOLD_MS);
  };

  const cancelHold = () => {
    if (phaseRef.current !== "holding") return;
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    setBoth("idle");
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startHold();
  };

  const onKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
      e.preventDefault();
      startHold();
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter" || e.key === " ") cancelHold();
  };

  const unlocked = phase === "unlocked";
  const hint = {
    idle: "Press and hold to unlock",
    holding: "Keep holding…",
    unlocking: "Unlocking…",
    unlocked: "",
  }[phase];

  return (<div className={styles.gaControl}>
    <div className={styles.gaDevice}>
      <span className={styles.gaDeviceIcon}><DoorIcon /></span>
      <div>
        <p className={`${styles.gaStatus} ${unlocked ? styles.gaStatusOn : ""}`}><span className={styles.gaDot} />{unlocked ? "Unlocked" : "Locked"}</p>
        <p className={styles.gaDeviceName}>Gate Door</p>
        <p className={styles.gaDeviceSub}>Walk-in door beside the gate</p>
      </div>
    </div>

    <div className={styles.gaHoldWrap}>
      <button
        type="button"
        aria-label="Press and hold to unlock the door"
        className={`${styles.gaHold} ${phase === "holding" ? styles.gaHolding : ""} ${unlocked ? styles.gaHoldDone : ""}`}
        disabled={phase === "unlocking" || unlocked}
        onPointerDown={onPointerDown}
        onPointerUp={cancelHold}
        onPointerCancel={cancelHold}
        onPointerLeave={cancelHold}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={cancelHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg className={styles.gaHoldRing} viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="46" /></svg>
        <KeyIcon size={34} />
      </button>
      <p className={styles.gaHint} aria-live="polite">
        {unlocked
          ? (<><strong>Door unlocked — Push to enter.</strong><br />Re-locks automatically after 10s.</>)
          : hint}
      </p>
    </div>
  </div>);
};
